/**
 * ItemsTab.tsx
 *
 * Patient-detail Items section matching legacy inventory-list.php.
 * Per-transaction disbursement log (inventory_transactions table joined to
 * quote_item_costs). Columns: Item, Quantity, Date.
 *
 *  - Row click opens ItemDisburseModal in read-only view mode (legacy parity:
 *    existing transactions are view-only).
 *  - The "Add Item" toolbar button is deliberately hidden. Disbursing straight
 *    from here bypasses the purchase flow: the item is never a payment
 *    material, so it never reaches the patient's Products, and stock is
 *    decremented with nothing sold or comped against it. Items should be
 *    purchased or comped through a payment instead. The create path below
 *    (`'NEW'`) is left wired up on purpose — this is a hold, not a removal,
 *    and the button is the only thing withdrawn.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Spinner } from '@/components/ui';
import { listInventoryTransactions } from '@/services/patient-inventory.service';
import { ItemDisburseModal } from './items/ItemDisburseModal';

function formatShortDate(value: string | null | undefined): string {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

interface ItemsTabProps {
  patientId: number;
}

export function ItemsTab({ patientId }: ItemsTabProps) {
  const [modalTx, setModalTx] = useState<number | 'NEW' | null>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['patient-inventory-transactions', patientId],
    queryFn: () => listInventoryTransactions(patientId),
  });

  const rows = data ?? [];

  return (
    <div className="space-y-4 py-2">
      {/* Toolbar keeps its shape so the withdrawn "Add Item" button (see the
          header comment) drops back in without a layout change. */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-900">Items</h2>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      ) : isError ? (
        <p className="py-4 text-sm text-red-600">Failed to load items.</p>
      ) : rows.length === 0 ? (
        <p className="py-4 text-sm text-slate-400 italic">there are no results</p>
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          <table className="min-w-full divide-y divide-slate-100 text-sm">
            <thead className="bg-slate-50">
              <tr className="text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                <th className="px-4 py-3">Item</th>
                <th className="px-4 py-3">Quantity</th>
                <th className="px-4 py-3">Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((tx) => (
                <tr
                  key={tx.transactionId}
                  className="hover:bg-slate-50 cursor-pointer"
                  onClick={() => setModalTx(tx.transactionId)}
                >
                  <td className="px-4 py-3 text-slate-800 font-medium">{tx.itemName}</td>
                  <td className="px-4 py-3 text-slate-700">{tx.quantityDisbursed}</td>
                  <td className="px-4 py-3 text-slate-700">{formatShortDate(tx.date)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modalTx !== null && (
        <ItemDisburseModal
          patientId={patientId}
          transactionId={modalTx}
          onClose={() => setModalTx(null)}
        />
      )}
    </div>
  );
}
